import React, { useState } from 'react';
import { Modal, Typography, Input, Form, Button, PhoneNumber } from '@crisellevaldez/speculo-ui';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

// Schema for test contact validation
const testContactSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  phoneNumber: z.string().min(10, 'Phone number must be at least 10 digits'),
});

export type TestContactInput = z.infer<typeof testContactSchema>;

interface AddTestContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: TestContactInput) => Promise<void>;
  isSubmitting?: boolean;
}

// Inner form component that resets when key changes
const TestContactForm: React.FC<{
  onSubmit: (data: TestContactInput) => Promise<void>;
  isSubmitting?: boolean;
  onCancel: () => void;
}> = ({ onSubmit, isSubmitting, onCancel }) => {
  const [formError, setFormError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TestContactInput>({
    resolver: zodResolver(testContactSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
    },
  });

  const onFormSubmit = async (data: TestContactInput) => {
    try {
      setFormError(null);
      await onSubmit(data);
      reset();
      setFormError(null);
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      console.error('Error submitting form:', error);
      const detail = error?.response?.data?.detail;
      if (detail) {
        setFormError(detail);
      } else if (error instanceof Error) {
        setFormError(error.message);
      } else {
        setFormError('Failed to add test contact');
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} data-tour="add-test-contact-form">
      <div className="space-y-4">
        {/* Form error message */}
        {formError && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 rounded-md">
            <p className="text-red-600 dark:text-red-400 text-sm">{formError}</p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Form.Control>
            <Form.Label required>First name</Form.Label>
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <Input {...field} placeholder="First name" error={errors.firstName?.message} />
              )}
            />
          </Form.Control>

          <Form.Control>
            <Form.Label required>Last name</Form.Label>
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <Input {...field} placeholder="Last name" error={errors.lastName?.message} />
              )}
            />
          </Form.Control>
        </div>

        <Form.Control>
          <Form.Label required>Phone number</Form.Label>
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field }) => (
              <PhoneNumber
                value={field.value}
                onChange={field.onChange}
                error={errors.phoneNumber?.message}
              />
            )}
          />
        </Form.Control>
      </div>

      <Modal.Footer>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" isLoading={isSubmitting}>
          Add Contact
        </Button>
      </Modal.Footer>
    </form>
  );
};

export const AddTestContactModal: React.FC<AddTestContactModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
}) => {
  // Key changes when modal opens to reset the form state
  const [formKey, setFormKey] = useState(0);

  const handleClose = () => {
    setFormKey((prev) => prev + 1); // Reset form on close
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <Modal.Header>
        <Typography variant="h3" className="font-bold">
          Add Test Contact
        </Typography>
      </Modal.Header>
      <Modal.Body>
        <TestContactForm
          key={formKey}
          onSubmit={onSubmit}
          isSubmitting={isSubmitting}
          onCancel={handleClose}
        />
      </Modal.Body>
    </Modal>
  );
};
